import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Globe, ArrowLeft, ArrowRight, ArrowUpDown, TrendingUp, BarChart3,
  DollarSign, ChevronDown, ChevronUp, Info, Search, Activity,
} from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useStateInternationalStats } from '@/hooks/useStateInternationalStats';
import { getCountryCode, getCountrySlug, COUNTRY_SLUGS } from '@/lib/countryUtils';
import { CountryCategoryBarChart } from '@/components/international/CountryCategoryBarChart';
import { CountryRadarChart } from '@/components/international/CountryRadarChart';
import { loadStaticData } from '@/lib/offlineData';
import { useQuery } from '@tanstack/react-query';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatUSD(amount: number): string {
  if (!amount || !isFinite(amount)) return '$0';
  if (amount >= 10000) return `$${(amount / 1000).toFixed(0)}k`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  if (amount >= 1) return `$${Math.round(amount).toLocaleString()}`;
  return `$${amount.toFixed(2)}`;
}

function formatMultiplier(m: number): string {
  if (!m || !isFinite(m)) return '0x';
  if (m >= 100) return `${Math.round(m)}x`;
  if (m >= 10) return `${m.toFixed(0)}x`;
  return `${m.toFixed(1)}x`;
}

function multiplierHex(m: number, isAhp: boolean): string {
  if (!m || !isFinite(m)) return '#888';
  if (isAhp) {
    // AHP patriotic: higher markup → more red (US overcharging).
    // Greens match InternationalHub's accessible tone — the old Tailwind
    // green-400/500 sat at 1.7-2.3:1 on the cream cards (axe serious x13).
    if (m >= 30) return '#0B6B49';
    if (m >= 15) return '#0F8A5F';
    if (m >= 8) return '#C5A55A';
    if (m >= 4) return '#e85d6c';
    return '#B22234';
  }
  // Luigi's
  if (m >= 30) return '#4ade80';
  if (m >= 15) return '#34d399';
  if (m >= 8) return '#facc15';
  if (m >= 4) return '#fb923c';
  return '#f87171';
}

function classifyProcedure(code: string): string {
  const num = parseInt(code, 10);
  if (isNaN(num)) return 'Other';
  if (num >= 10000 && num <= 19999) return 'Surgery';
  if (num >= 20000 && num <= 29999) return 'Musculoskeletal';
  if (num >= 30000 && num <= 39999) return 'Respiratory/Cardiovascular';
  if (num >= 40000 && num <= 49999) return 'Digestive';
  if (num >= 50000 && num <= 59999) return 'Urinary/Reproductive';
  if (num >= 60000 && num <= 69999) return 'Nervous System';
  if (num >= 70000 && num <= 79999) return 'Radiology';
  if (num >= 80000 && num <= 89999) return 'Pathology/Lab';
  if (num >= 90000 && num <= 99999) return 'Medicine/E&M';
  return 'Other';
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CountryDetail() {
  const { countrySlug } = useParams<{ countrySlug: string }>();
  const navigate = useNavigate();
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  // ── AHP patriotic color system ──────────────────────────────────────────
  const pageBg = isAhp ? (isDark ? '#0a1628' : '#FAF9F6') : undefined;
  // Elevated panel (matches the hospital-profile aesthetic): distinct navy
  // panel on the darker page bg — the old rgba(10,22,40,.85) was nearly the
  // page color itself, so every section read as flat unstyled dark.
  const cardBg = isAhp
    ? (isDark ? '#0f1d2f' : '#ffffff')
    : undefined;
  const borderColor = isAhp
    ? (isDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,0,0,0.1)')
    : undefined;
  const textColor = isAhp
    ? (isDark ? '#FAF9F6' : '#1B1B1B')
    : undefined;
  const mutedText = isAhp
    ? (isDark ? 'rgba(250,249,246,0.6)' : 'rgba(0,0,0,0.5)')
    : undefined;
  const accentColor = isAhp ? '#C5A55A' : '#00ff41';
  const navy = isAhp ? (isDark ? '#7da6d4' : '#002868') : '#3b82f6';
  const red = isAhp ? (isDark ? '#e85d6c' : '#B22234') : '#ef4444';

  const countryCode = countrySlug ? getCountryCode(countrySlug) : undefined;

  const {
    countries: countryMap,
    procedurePrices,
    procedureNames,
    usPricing,
    scenarioCosts,
    isLoading,
  } = useStateInternationalStats();

  // Load scenario names
  const { data: scenarioData } = useQuery({
    queryKey: ['scenarios-index-for-country'],
    queryFn: async () => {
      const d = await loadStaticData<{ data: Array<{ id: string; name: string; slug?: string }> }>('scenarios.json');
      return d?.data || [];
    },
    staleTime: Infinity,
  });

  const scenarioNameMap = useMemo(() => {
    const m = new Map<string, { name: string; slug: string }>();
    if (scenarioData) {
      for (const s of scenarioData) {
        const slug = s.slug || s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        m.set(s.id, { name: s.name, slug });
      }
    }
    return m;
  }, [scenarioData]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [procSearch, setProcSearch] = useState('');
  const [procPage, setProcPage] = useState(0);
  // Default to US-price impact: raw-multiplier ordering surfaced tariff-unit
  // artifacts (a $11 tariff line vs a $43k US total = 3,900x) as the headline
  // rows. Multiplier stays one click away.
  const [procSort, setProcSort] = useState<'multiplier' | 'us_gross' | 'intl_price' | 'name'>('us_gross');
  const [procSortDir, setProcSortDir] = useState<'asc' | 'desc'>('desc');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const PROC_PAGE_SIZE = 20;

  // ── Redirect if invalid slug ──────────────────────────────────────────────
  if (!countrySlug || !countryCode) {
    if (typeof window !== 'undefined') navigate('/international', { replace: true });
    return null;
  }

  const country = countryMap.get(countryCode);

  // ── Lookup maps ─────────────────────────────────────────────────────────
  const usMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of usPricing) m.set(u.cpt_code, u.avg_gross_charge);
    return m;
  }, [usPricing]);

  const nameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of procedureNames) m.set(p.code, p.llm_quick_name || p.name);
    return m;
  }, [procedureNames]);

  // ── Country-specific procedures ───────────────────────────────────────────
  const countryProcedures = useMemo(() => {
    if (!countryCode) return [];
    const filtered = procedurePrices.filter(p => p.country_code === countryCode);
    return filtered.map(p => {
      const usGross = Number(usMap.get(p.us_code)) || 0;
      const intlPrice = Number(p.price_usd) || 0;
      const mult = usGross > 0 && intlPrice > 0 ? usGross / intlPrice : 0;
      return {
        us_code: p.us_code,
        name: nameMap.get(p.us_code) || p.us_code,
        intl_price: intlPrice,
        us_gross: usGross,
        multiplier: mult,
        category: classifyProcedure(p.us_code),
      };
    }).filter(p => p.multiplier > 0 && isFinite(p.multiplier));
  }, [countryCode, procedurePrices, usMap, nameMap]);

  // ── Aggregate stats ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!countryProcedures.length) return null;
    const multipliers = countryProcedures.map(p => p.multiplier);
    const sorted = [...multipliers].sort((a, b) => a - b);
    const avgMult = multipliers.reduce((a, b) => a + b, 0) / multipliers.length;
    const medMult = sorted[Math.floor(sorted.length / 2)];
    const avgIntl = countryProcedures.reduce((s, p) => s + p.intl_price, 0) / countryProcedures.length;
    const avgUS = countryProcedures.reduce((s, p) => s + p.us_gross, 0) / countryProcedures.length;
    return { avgMult, medMult, avgIntl, avgUS, count: countryProcedures.length };
  }, [countryProcedures]);

  // ── Category breakdown ────────────────────────────────────────────────────
  const categoryData = useMemo(() => {
    const buckets: Record<string, { multipliers: number[]; intlPrices: number[]; usPrices: number[] }> = {};
    for (const p of countryProcedures) {
      if (!buckets[p.category]) buckets[p.category] = { multipliers: [], intlPrices: [], usPrices: [] };
      buckets[p.category].multipliers.push(p.multiplier);
      buckets[p.category].intlPrices.push(p.intl_price);
      buckets[p.category].usPrices.push(p.us_gross);
    }
    return Object.entries(buckets)
      .filter(([, v]) => v.multipliers.length >= 3)
      .map(([cat, v]) => ({
        category: cat,
        multiplier: v.multipliers.reduce((a, b) => a + b, 0) / v.multipliers.length,
        countryAvg: v.intlPrices.reduce((a, b) => a + b, 0) / v.intlPrices.length,
        usAvg: v.usPrices.reduce((a, b) => a + b, 0) / v.usPrices.length,
        procedureCount: v.multipliers.length,
      }))
      .sort((a, b) => b.multiplier - a.multiplier);
  }, [countryProcedures]);

  // ── Scenario costs ────────────────────────────────────────────────────────
  const countryScenarios = useMemo(() => {
    if (!countryCode) return [];
    return scenarioCosts
      .filter(s => s.country_code === countryCode && s.mapped_cpt_codes > 0)
      .map(s => {
        const info = scenarioNameMap.get(s.scenario_id);
        const usCost = Number(s.us_national_total_cost) || 0;
        const intlCost = Number(s.intl_total_cost) || 0;
        const mult = usCost > 0 && intlCost > 0 ? usCost / intlCost : 0;
        return {
          ...s,
          us_national_total_cost: usCost,
          intl_total_cost: intlCost,
          name: info?.name || s.scenario_id,
          slug: info?.slug || '',
          multiplier: mult,
        };
      })
      .filter(s => s.multiplier > 0 && isFinite(s.multiplier))
      .sort((a, b) => b.multiplier - a.multiplier);
  }, [countryCode, scenarioCosts, scenarioNameMap]);

  // ── Filtered + sorted procedures ──────────────────────────────────────────
  // Rows with multipliers past the methodology's 100x winsorization bound are
  // almost always tariff-unit mismatches (a per-point tariff line vs a US
  // total) — hide them from the default view, disclose the count below.
  const artifactCount = useMemo(
    () => countryProcedures.filter(p => p.multiplier > 100).length,
    [countryProcedures]);
  const filteredProcs = useMemo(() => {
    let procs = countryProcedures.filter(p => p.multiplier <= 100);
    if (procSearch) {
      const q = procSearch.toLowerCase();
      procs = procs.filter(p => p.name.toLowerCase().includes(q) || p.us_code.includes(q));
    }
    return [...procs].sort((a, b) => {
      let va: number, vb: number;
      if (procSort === 'name') {
        return procSortDir === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      va = a[procSort]; vb = b[procSort];
      return procSortDir === 'asc' ? va - vb : vb - va;
    });
  }, [countryProcedures, procSearch, procSort, procSortDir]);

  const totalProcPages = Math.ceil(filteredProcs.length / PROC_PAGE_SIZE);
  const displayedProcs = filteredProcs.slice(procPage * PROC_PAGE_SIZE, (procPage + 1) * PROC_PAGE_SIZE);

  const handleProcSort = (field: typeof procSort) => {
    if (procSort === field) {
      setProcSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setProcSort(field);
      setProcSortDir('desc');
    }
    setProcPage(0);
  };

  // ── Navigation between countries ──────────────────────────────────────────
  const countryCodes = Object.keys(COUNTRY_SLUGS);
  const currentIdx = countryCodes.indexOf(countryCode);
  const prevSlug = currentIdx > 0 ? getCountrySlug(countryCodes[currentIdx - 1]) : null;
  const nextSlug = currentIdx < countryCodes.length - 1 ? getCountrySlug(countryCodes[currentIdx + 1]) : null;

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 pb-24"
        style={{ background: pageBg, color: textColor }}>
        <div className="flex gap-2">
          {[0, 160, 320].map((delay) => (
            <div key={delay} className="w-2 h-2 rounded-full animate-bounce"
              style={{ background: accentColor, animationDelay: `${delay}ms` }} />
          ))}
        </div>
        <p className="font-mono text-sm" style={{ color: mutedText }}>Loading country data...</p>
      </div>
    );
  }

  if (!country || !stats) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 pb-24"
        style={{ background: pageBg, color: textColor }}>
        <Globe className="w-12 h-12" style={{ color: mutedText }} />
        <p className="text-lg font-medium">Country not found</p>
        <Link to="/international" style={{ color: accentColor }} className="text-sm hover:underline">
          Back to international comparison
        </Link>
      </div>
    );
  }

  const countryName = country.country_name;
  const flag = country.flag_emoji || '';

  // Category colors for the breakdown table
  // Category pills — restrained site palette (navy/gold/red family) instead
  // of the old rainbow solids that clashed with the page aesthetic.
  const catPill = (accent: string) => ({
    bg: isDark ? `${accent}26` : `${accent}14`,
    text: isDark ? '#F5EBD7' : accent,
    border: `${accent}55`,
  });
  const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
    'Surgery': catPill('#B22234'),
    'Musculoskeletal': catPill('#8a5a2c'),
    'Respiratory/Cardiovascular': catPill('#B22234'),
    'Digestive': catPill('#8a5a2c'),
    'Urinary/Reproductive': catPill('#002868'),
    'Nervous System': catPill('#002868'),
    'Radiology': catPill('#0F6B8A'),
    'Pathology/Lab': catPill('#0F8A5F'),
    'Medicine/E&M': catPill('#0F8A5F'),
    'Other': catPill('#5a6472'),
  };

  // ── Common section styles ───────────────────────────────────────────────
  const sectionCardStyle: React.CSSProperties = {
    background: cardBg,
    border: `1px solid ${borderColor}`,
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: isAhp ? (isDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)') : undefined,
  };

  const sectionHeadingStyle: React.CSSProperties = {
    color: textColor,
    fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
    fontWeight: isAhp ? 400 : 600,
    fontSize: '1.35rem',
  };

  const tableHeaderBg = isAhp
    ? (isDark ? 'rgba(0,20,60,0.8)' : 'rgba(0,40,104,0.06)')
    : undefined;

  const rowHoverBg = isAhp
    ? (isDark ? 'rgba(197,165,90,0.06)' : 'rgba(0,40,104,0.03)')
    : undefined;

  return (
    <div className="min-h-screen pb-24"
      style={{ background: pageBg, color: textColor }}>
      <Helmet>
        <title>{countryName} vs US Healthcare Prices | {brand.name}</title>
        <meta name="description" content={`Compare ${countryName} healthcare prices with US hospital charges. US hospitals charge ${stats.avgMult.toFixed(1)}x more on average for the same procedures. See ${stats.count.toLocaleString()} procedure price comparisons.`} />
        <meta name="keywords" content={`${countryName} healthcare prices, US vs ${countryName} medical costs, ${countryName} hospital prices, international healthcare comparison`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/international/${countrySlug}`} />
        <meta property="og:title" content={`${countryName} vs US Healthcare Prices`} />
        <meta property="og:description" content={`US hospitals charge ${stats.avgMult.toFixed(1)}x more than ${countryName} for the same medical procedures.`} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${brand.primaryDomain}/international/${countrySlug}`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${countryName} vs US Healthcare Prices`} />
        <meta name="twitter:description" content={`US hospitals charge ${stats.avgMult.toFixed(1)}x more than ${countryName} for the same medical procedures.`} />
        <meta name="twitter:image" content={brand.social.ogImage} />
      </Helmet>

      {/* ── Header + Breadcrumb ───────────────────────────────────────────── */}
      <div className="relative overflow-hidden"
        style={{
          borderBottom: `1px solid ${borderColor}`,
          background: isAhp
            ? (isDark
              ? 'linear-gradient(135deg, rgba(0,20,60,0.6) 0%, rgba(10,22,40,0.9) 50%, rgba(178,34,52,0.1) 100%)'
              : 'linear-gradient(135deg, rgba(0,40,104,0.04) 0%, rgba(250,249,246,1) 50%, rgba(178,34,52,0.03) 100%)')
            : undefined,
        }}>
        <div className="relative w-full px-4 sm:px-6 py-6 sm:py-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs mb-4" style={{ color: mutedText }}>
            <Link to="/international" className="hover:opacity-80 transition-opacity flex items-center gap-1"
              style={{ color: accentColor }}>
              <ArrowLeft className="w-3 h-3" />
              International Comparison
            </Link>
            <span>/</span>
            <span style={{ color: textColor }}>{flag} {countryName}</span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">{flag}</span>
                <div>
                  <h1 className="text-2xl sm:text-3xl tracking-tight"
                    style={{
                      color: textColor,
                      fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
                      fontWeight: isAhp ? 400 : 700,
                    }}>
                    {countryName}
                  </h1>
                  <p className="text-sm" style={{ color: mutedText }}>
                    {country.healthcare_system}
                    {country.fee_schedule_name && ` \u2022 ${country.fee_schedule_name}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Multiplier badge */}
            <div style={{
              background: isAhp
                ? (isDark ? 'rgba(178,34,52,0.15)' : 'rgba(178,34,52,0.08)')
                : 'rgba(239,68,68,0.1)',
              border: `1px solid ${isAhp ? (isDark ? 'rgba(178,34,52,0.3)' : 'rgba(178,34,52,0.2)') : 'rgba(239,68,68,0.3)'}`,
              borderRadius: '12px',
              padding: '12px 20px',
              textAlign: 'center' as const,
              flexShrink: 0,
            }}>
              <div className="text-xs uppercase tracking-wider font-medium" style={{ color: red }}>US Charges</div>
              <div className="text-3xl sm:text-4xl font-black tabular-nums"
                style={{ color: multiplierHex(stats.avgMult, isAhp) }}>
                {formatMultiplier(stats.avgMult)}
              </div>
              <div className="text-xs" style={{ color: mutedText }}>more on average</div>
            </div>
          </div>

          {/* Cost summary — hospital-profile-style stat chips: elevated panels
              with per-stat color-coded borders and uppercase labels. */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(() => {
              const chip = (accent: string): React.CSSProperties => ({
                background: cardBg,
                border: `1px solid ${accent}55`,
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.35)' : '0 2px 12px rgba(0,0,0,0.05)',
              });
              const label: React.CSSProperties = {
                fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: mutedText,
              };
              const green = isDark ? '#6bc9a0' : '#2b5e49';
              return (
                <>
                  <div style={chip(green)}>
                    <div style={label}>Avg {countryName} Price</div>
                    <div className="text-xl font-bold tabular-nums" style={{ color: green, fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontSize: '1.5rem' }}>{formatUSD(stats.avgIntl)}</div>
                  </div>
                  <div style={chip(red || '#B22234')}>
                    <div style={label}>Avg US Price</div>
                    <div className="text-xl font-bold tabular-nums" style={{ color: red, fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontSize: '1.5rem' }}>{formatUSD(stats.avgUS)}</div>
                  </div>
                  <div style={chip(multiplierHex(stats.medMult, isAhp))}>
                    <div style={label}>Median Markup</div>
                    <div className="text-xl font-bold tabular-nums" style={{ color: multiplierHex(stats.medMult, isAhp), fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontSize: '1.5rem' }}>{formatMultiplier(stats.medMult)}</div>
                  </div>
                  <div style={chip(accentColor || '#C5A55A')}>
                    <div style={label}>Procedures Compared</div>
                    <div className="text-xl font-bold tabular-nums" style={{ color: accentColor, fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontSize: '1.5rem' }}>{stats.count.toLocaleString()}</div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 py-6 space-y-8">

        {/* ── Category Bar Chart ───────────────────────────────────────────── */}
        {categoryData.length > 0 && (
          <section>
            <h2 className="mb-2 flex items-center gap-2" style={sectionHeadingStyle}>
              <BarChart3 className="w-5 h-5" style={{ color: navy }} />
              US vs {countryName}: Price by Category
            </h2>
            <p className="text-xs mb-4" style={{ color: mutedText }}>
              Average procedure cost in each medical category. Red = US, {isAhp ? 'Blue' : 'Green'} = {countryName}.
            </p>
            <div style={sectionCardStyle}>
              <div style={{ padding: '16px' }}>
                <CountryCategoryBarChart
                  data={categoryData}
                  countryName={countryName}
                />
              </div>
            </div>
          </section>
        )}

        {/* ── Radar Chart ─────────────────────────────────────────────────── */}
        {categoryData.length >= 4 && (
          <section>
            <h2 className="mb-2 flex items-center gap-2" style={sectionHeadingStyle}>
              <Activity className="w-5 h-5" style={{ color: accentColor }} />
              Markup by Category
            </h2>
            <p className="text-xs mb-4" style={{ color: mutedText }}>
              How many times more US hospitals charge vs {countryName} by medical specialty.
            </p>
            <div style={sectionCardStyle}>
              <div style={{ padding: '16px' }}>
                <CountryRadarChart
                  data={categoryData}
                  countryName={countryName}
                />
              </div>
            </div>
          </section>
        )}

        {/* ── Category Breakdown Table ─────────────────────────────────────── */}
        {categoryData.length > 0 && (
          <section>
            <h2 className="mb-2 flex items-center gap-2" style={sectionHeadingStyle}>
              <TrendingUp className="w-5 h-5" style={{ color: accentColor }} />
              Category Breakdown
            </h2>
            <p className="text-xs mb-4" style={{ color: mutedText }}>
              All medical categories with US markup for {countryName}.
            </p>

            <div style={sectionCardStyle}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: tableHeaderBg }}>
                    {['Category', 'Procedures', `Avg ${countryName}`, 'Avg US', 'Markup'].map((h, i) => (
                      <th key={h} style={{
                        padding: '10px 16px',
                        textAlign: i === 0 ? 'left' : 'right',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: mutedText,
                        borderBottom: `1px solid ${borderColor}`,
                        textTransform: 'uppercase' as const,
                        letterSpacing: '0.05em',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(showAllCategories ? categoryData : categoryData.slice(0, 8)).map((cat, idx) => (
                    <tr key={cat.category}
                      style={{
                        borderBottom: `1px solid ${borderColor}`,
                        background: idx % 2 === 0 ? 'transparent' : (isAhp ? (isDark ? 'rgba(0,20,60,0.2)' : 'rgba(0,40,104,0.02)') : undefined),
                      }}>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: (categoryColors[cat.category] || categoryColors['Other']).bg,
                          color: (categoryColors[cat.category] || categoryColors['Other']).text,
                          border: `1px solid ${(categoryColors[cat.category] || categoryColors['Other']).border}`,
                        }}>
                          {cat.category}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: textColor }}>
                        {cat.procedureCount}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: textColor }}>
                        {formatUSD(cat.countryAvg)}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: red }}>
                        {formatUSD(cat.usAvg)}
                      </td>
                      <td style={{
                        padding: '10px 16px',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 700,
                        fontSize: '13px',
                        color: multiplierHex(cat.multiplier, isAhp),
                      }}>
                        {formatMultiplier(cat.multiplier)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {categoryData.length > 8 && (
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  onClick={() => setShowAllCategories(!showAllCategories)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: accentColor,
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {showAllCategories ? 'Show Less' : `Show All ${categoryData.length} Categories`}
                  {showAllCategories ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
            )}
          </section>
        )}

        {/* ── Top Overpriced Procedures ─────────────────────────────────────── */}
        <section>
          <h2 className="mb-2 flex items-center gap-2" style={sectionHeadingStyle}>
            <DollarSign className="w-5 h-5" style={{ color: red }} />
            Procedure Price Comparison
          </h2>
          <p className="text-xs mb-4" style={{ color: mutedText }}>
            {filteredProcs.length.toLocaleString()} procedures compared between {countryName} and US hospitals.{artifactCount > 0 ? ` ${artifactCount.toLocaleString()} rows with >100x ratios are hidden — those are almost always tariff-unit mismatches, not real prices.` : ''}
          </p>

          {/* Search */}
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: mutedText }} />
            <input
              type="text"
              placeholder="Search procedures..."
              style={{
                width: '100%',
                paddingLeft: '36px',
                paddingRight: '12px',
                paddingTop: '8px',
                paddingBottom: '8px',
                fontSize: '14px',
                background: isAhp ? (isDark ? 'rgba(0,20,60,0.5)' : 'rgba(0,40,104,0.04)') : undefined,
                border: `1px solid ${borderColor}`,
                borderRadius: '8px',
                outline: 'none',
                color: textColor,
              }}
              className={isAhp ? '' : 'bg-muted/30 border border-border/30 rounded-md'}
              value={procSearch}
              onChange={e => { setProcSearch(e.target.value); setProcPage(0); }}
            />
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0" style={sectionCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: tableHeaderBg }}>
                  {[
                    { label: 'Procedure', field: 'name' as const, align: 'left' },
                    { label: countryName, field: 'intl_price' as const, align: 'right' },
                    { label: 'US Avg', field: 'us_gross' as const, align: 'right' },
                    { label: 'Markup', field: 'multiplier' as const, align: 'right' },
                  ].map(col => (
                    <th key={col.field}
                      onClick={() => handleProcSort(col.field)}
                      style={{
                        padding: '10px 16px',
                        textAlign: col.align as any,
                        fontSize: '12px',
                        fontWeight: 600,
                        color: mutedText,
                        borderBottom: `1px solid ${borderColor}`,
                        cursor: 'pointer',
                        userSelect: 'none',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: col.field === 'name' ? '200px' : undefined,
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start', gap: '4px' }}>
                        {col.label} <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedProcs.map((proc, idx) => (
                  <tr key={proc.us_code}
                    style={{
                      borderBottom: `1px solid ${borderColor}`,
                      background: idx % 2 === 0 ? 'transparent' : (isAhp ? (isDark ? 'rgba(0,20,60,0.2)' : 'rgba(0,40,104,0.02)') : undefined),
                    }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>
                      <Link
                        to={`/cpt/${proc.us_code}`}
                        style={{ color: accentColor, textDecoration: 'none' }}
                        className="hover:underline"
                      >
                        <div style={{ fontSize: '13px', lineHeight: 1.3 }}>{proc.name}</div>
                        <div style={{ fontSize: '10px', color: mutedText }}>CPT {proc.us_code}</div>
                      </Link>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'monospace', fontSize: '13px', fontVariantNumeric: 'tabular-nums', color: textColor }}>
                      {formatUSD(proc.intl_price)}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: red }}>
                      {formatUSD(proc.us_gross)}
                    </td>
                    <td style={{
                      padding: '10px 16px',
                      textAlign: 'right',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '13px',
                      color: multiplierHex(proc.multiplier, isAhp),
                    }}>
                      {formatMultiplier(proc.multiplier)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalProcPages > 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '16px',
            }}>
              <button
                disabled={procPage === 0}
                onClick={() => setProcPage(p => p - 1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: procPage === 0 ? (mutedText || '#666') : accentColor,
                  fontSize: '13px',
                  cursor: procPage === 0 ? 'default' : 'pointer',
                  opacity: procPage === 0 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '12px', color: mutedText }}>
                Page {procPage + 1} of {totalProcPages} ({filteredProcs.length.toLocaleString()} procedures)
              </span>
              <button
                disabled={procPage >= totalProcPages - 1}
                onClick={() => setProcPage(p => p + 1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: procPage >= totalProcPages - 1 ? (mutedText || '#666') : accentColor,
                  fontSize: '13px',
                  cursor: procPage >= totalProcPages - 1 ? 'default' : 'pointer',
                  opacity: procPage >= totalProcPages - 1 ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          )}
        </section>

        {/* ── Scenario Costs ──────────────────────────────────────────────── */}
        {countryScenarios.length > 0 && (
          <section>
            <h2 className="mb-2 flex items-center gap-2" style={sectionHeadingStyle}>
              <Globe className="w-5 h-5" style={{ color: accentColor }} />
              Medical Scenario Costs: {countryName} vs US
            </h2>
            <p className="text-xs mb-4" style={{ color: mutedText }}>
              Estimated total costs for common medical scenarios in {countryName} compared to US national averages.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {countryScenarios.slice(0, 12).map(s => (
                <div key={s.scenario_id}
                  style={{
                    ...sectionCardStyle,
                    padding: '16px',
                    transition: 'border-color 0.2s',
                  }}
                  className="hover:opacity-95"
                >
                  {s.slug ? (
                    <Link to={`/scenarios/${s.scenario_id}/${s.slug}`}
                      style={{ color: accentColor, textDecoration: 'none' }}
                      className="hover:underline">
                      <h3 style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', lineHeight: 1.3 }}>{s.name}</h3>
                    </Link>
                  ) : (
                    <h3 style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', lineHeight: 1.3, color: textColor }}>{s.name}</h3>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '10px', color: mutedText }}>{countryName}</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: navy }}>
                        {formatUSD(s.intl_total_cost)}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 900,
                      fontVariantNumeric: 'tabular-nums',
                      color: multiplierHex(s.multiplier, isAhp),
                    }}>
                      {formatMultiplier(s.multiplier)}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '10px', color: red }}>US</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: red }}>
                        {formatUSD(s.us_national_total_cost)}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '10px', color: mutedText, marginTop: '8px' }}>
                    {s.mapped_cpt_codes}/{s.total_cpt_codes} procedures mapped
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Methodology Note ─────────────────────────────────────────────── */}
        <div style={{
          ...sectionCardStyle,
          background: isAhp
            ? (isDark ? 'rgba(0,20,60,0.4)' : 'rgba(0,40,104,0.03)')
            : cardBg,
          padding: '16px',
        }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Info className="w-5 h-5 shrink-0 mt-0.5" style={{ color: mutedText }} />
            <div style={{ fontSize: '12px', color: mutedText }}>
              <p style={{ fontWeight: 500, color: isAhp ? (isDark ? 'rgba(250,249,246,0.8)' : 'rgba(0,0,0,0.7)') : undefined, marginBottom: '8px' }}>
                About this comparison
              </p>
              <p style={{ marginBottom: '6px' }}>
                <strong style={{ color: textColor }}>US prices</strong> are average gross charges (sticker prices) from hospital chargemasters across {'>'}5,000 US hospitals.
              </p>
              <p style={{ marginBottom: '8px' }}>
                <strong style={{ color: textColor }}>{countryName} prices</strong> are from the {country.fee_schedule_name || 'national fee schedule'}
                {country.healthcare_system && ` (${country.healthcare_system} system)`}.
                These represent what the healthcare system pays providers, converted to USD.
              </p>
              <Link to="/data-methodology"
                style={{ color: accentColor, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                className="hover:underline">
                Full methodology <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>

        {/* ── Country Navigation ───────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '16px',
          borderTop: `1px solid ${borderColor}`,
        }}>
          {prevSlug ? (
            <Link
              to={`/international/${prevSlug}`}
              style={{ fontSize: '13px', color: mutedText, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              className="hover:opacity-80"
            >
              <ArrowLeft className="w-4 h-4" />
              {countryMap.get(countryCodes[currentIdx - 1])?.flag_emoji}{' '}
              {countryMap.get(countryCodes[currentIdx - 1])?.country_name || prevSlug}
            </Link>
          ) : <div />}
          <Link
            to="/international"
            style={{ fontSize: '13px', color: accentColor, textDecoration: 'none' }}
            className="hover:underline"
          >
            All Countries
          </Link>
          {nextSlug ? (
            <Link
              to={`/international/${nextSlug}`}
              style={{ fontSize: '13px', color: mutedText, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              className="hover:opacity-80"
            >
              {countryMap.get(countryCodes[currentIdx + 1])?.flag_emoji}{' '}
              {countryMap.get(countryCodes[currentIdx + 1])?.country_name || nextSlug}
              <ArrowRight className="w-4 h-4" />
            </Link>
          ) : <div />}
        </div>
      </div>
    </div>
  );
}
